import { format, isValid, parseISO } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import type { BusinessTrip } from '@/hooks/trips/types';
import { cn } from '@/lib/utils';

export type KnownStatus = Exclude<BusinessTrip['status'], 'unknown'>;

export const STATUS_STYLES: Record<KnownStatus, { className: string; translationKey: string }> = {
  draft: {
    className:
      'rounded border-none bg-[#f1f1f1] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#292929] hover:bg-[#f1f1f1]',
    translationKey: 'statuses.draft',
  },
  pending: {
    className:
      'rounded border-none bg-[rgba(255,193,7,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#f57c00] hover:bg-[rgba(255,193,7,0.08)]',
    translationKey: 'statuses.pending',
  },
  approved: {
    className:
      'rounded border-none bg-[rgba(2,186,60,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#299764] hover:bg-[rgba(2,186,60,0.08)]',
    translationKey: 'statuses.approved',
  },
  declined: {
    className:
      'rounded border-none bg-[rgba(255,112,112,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#e5484d] hover:bg-[rgba(255,112,112,0.08)]',
    translationKey: 'statuses.declined',
  },
  returned_back: {
    className:
      'rounded border-none bg-[rgba(2,128,255,0.06)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#0880ea] hover:bg-[rgba(2,128,255,0.06)]',
    translationKey: 'statuses.returnedBack',
  },
  cancelled: {
    className:
      'rounded border-none bg-[rgba(108,117,125,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#6c757d] hover:bg-[rgba(108,117,125,0.08)]',
    translationKey: 'statuses.cancelled',
  },
  extended: {
    className:
      'rounded border-none bg-[rgba(0,188,212,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#00bcd4] hover:bg-[rgba(0,188,212,0.08)]',
    translationKey: 'statuses.extended',
  },
  shortened: {
    className:
      'rounded border-none bg-[rgba(255,87,34,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#ff5722] hover:bg-[rgba(255,87,34,0.08)]',
    translationKey: 'statuses.shortened',
  },
  finalizing: {
    className:
      'rounded border-none bg-[rgba(156,39,176,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#9c27b0] hover:bg-[rgba(156,39,176,0.08)]',
    translationKey: 'statuses.finalizing',
  },
  finished: {
    className:
      'rounded border-none bg-[rgba(76,175,80,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#4caf50] hover:bg-[rgba(76,175,80,0.08)]',
    translationKey: 'statuses.finished',
  },
};

export const DATE_PLACEHOLDER = '—';

export function getStatusBadge(
  status: BusinessTrip['status'],
  t: (key: string) => string,
  labelOverride?: string | null,
  options?: { className?: string }
) {
  if (status === 'unknown' || !(status in STATUS_STYLES)) {
    return <Badge variant="secondary">{status}</Badge>;
  }

  const { className, translationKey } = STATUS_STYLES[status as KnownStatus];
  return (
    <Badge className={cn(className, options?.className)}>
      {labelOverride ?? t(translationKey)}
    </Badge>
  );
}

export function formatDateRange(startDate: string, endDate: string) {
  const formatDate = (dateStr: string) => {
    const parsed = parseISO(dateStr);
    if (!isValid(parsed)) {
      return dateStr;
    }
    return format(parsed, 'dd/MM/yyyy');
  };

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

function normalizeDateInput(value: string) {
  if (value.includes(' ') && !value.includes('T')) {
    return value.replace(' ', 'T');
  }
  return value;
}

export function formatSubmittedDate(value?: string) {
  if (!value) {
    return DATE_PLACEHOLDER;
  }

  const normalized = normalizeDateInput(value);
  const parsed = parseISO(normalized);

  if (isValid(parsed)) {
    return format(parsed, 'dd/MM/yyyy');
  }

  const fallback = new Date(value);
  if (!Number.isNaN(fallback.getTime())) {
    return format(fallback, 'dd/MM/yyyy');
  }

  return value;
}
